import type { ThemeName } from "../Hooks/useTheme";

/**
 * AntD-facing mirror of the CSS custom properties declared in
 * `styles/index.scss`.
 *
 * Why a mirror exists: SCSS cannot be imported into TS, and AntD's
 * ConfigProvider needs concrete values synchronously at render time — reading
 * them back with getComputedStyle would paint one frame with AntD's defaults.
 *
 * The two are kept honest by `assertTokensInSync()` below, which runs in dev
 * only and logs a warning if a value here drifts from the stylesheet.
 *
 * Source of truth for the values themselves: Figma "crm-dashboard", frames
 * `analysis` (light, 1:2) and `analysis - dark` (dark, 245:1115).
 */
export const themeTokens = {
  lightTheme: {
    "--bg-app": "#f0f1f5",
    "--bg-surface": "#ffffff",
    "--bg-surface-raised": "#ffffff",
    "--text-primary": "#000000",
    "--text-secondary": "#323232",
    "--border-default": "#dadada",
    "--border-subtle": "#e8e8e8",
  },
  darkTheme: {
    "--bg-app": "#141b2d",
    "--bg-surface": "#1f2940",
    "--bg-surface-raised": "#26324b",
    "--text-primary": "#ffffff",
    "--text-secondary": "#d3d3d3",
    "--border-default": "rgba(218, 218, 218, 0.16)",
    "--border-subtle": "rgba(218, 218, 218, 0.08)",
  },
} as const satisfies Record<ThemeName, Record<string, string>>;

/** Theme-invariant, declared once on :root in the stylesheet. */
export const brandTokens = {
  brand: "#0586e2",
  textMuted: "#83829d",
  radiusCard: 5,
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
} as const;

/** Figma chart series palette — identical in both themes. */
export const chartSeries = [
  "#6560f0",
  "#dc3545",
  "#00b2ff",
  "#ffc700",
  "#06b48a",
] as const;

/**
 * Dev-only guard against this file drifting from styles/index.scss.
 * Compares each mirrored token against its computed CSS value.
 */
export function assertTokensInSync(theme: ThemeName) {
  if (!import.meta.env.DEV) return;

  const computed = getComputedStyle(document.documentElement);
  const drift: string[] = [];

  for (const [name, expected] of Object.entries(themeTokens[theme])) {
    const actual = computed.getPropertyValue(name).trim();
    if (!actual) {
      drift.push(`${name} is not declared in the stylesheet`);
      continue;
    }
    if (normalize(actual) !== normalize(expected)) {
      drift.push(
        `${name}: stylesheet has "${actual}", tokens.ts has "${expected}"`
      );
    }
  }

  if (drift.length) {
    console.warn(
      `[tokens] styles/tokens.ts is out of sync with styles/index.scss for "${theme}":\n  ` +
        drift.join("\n  ")
    );
  }
}

const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, "");
